import type {PageObject} from '@PosterWhiteboard/page/page.types';
import {AnimationType, ITEM_TYPE} from '@PosterWhiteboard/items/item/item.types';
import type {SlideshowItemObject} from '@PosterWhiteboard/items/slideshow-item/slideshow-item.class';
import {v4 as uuidv4} from 'uuid';
import type {ItemObject, SlideItemObject} from '@PosterWhiteboard/items/item/item.types';
import type {SubtitleObject} from '@PosterWhiteboard/items/transcript-item/subtitle/subtitle.types';
import type {TranscriptItemObject} from '@PosterWhiteboard/items/transcript-item/transcript-item.types';
import type {RectangleItemObject} from '@PosterWhiteboard/items/rectangle-item/rectangle-item';
import type {VideoItemObject} from '@PosterWhiteboard/items/video-item/video-item.types';
import type {ImageItemObject} from '@PosterWhiteboard/items/image-item/image-item.class';
import {transparencyStates} from '@PosterWhiteboard/items/image-item/image-item.class';
import {FillTypes} from '@PosterWhiteboard/classes/fill.class';
import {convertFabricColorToFillObject} from '@Utils/color.util';
import {PAGE_END_TIME_TOLERANCE_SECONDS} from '@PosterWhiteboard/poster/poster.types';

export const isImageBackgroundObject = (item: ItemObject | undefined): item is ImageItemObject => {
  return !!item?.isBackground && item.gitype === ITEM_TYPE.IMAGE;
};

export const isVideoBackgroundObject = (item: ItemObject | undefined): item is VideoItemObject => {
  return !!item?.isBackground && item.gitype === ITEM_TYPE.VIDEO;
};

export const isColorBackgroundObject = (item: ItemObject | undefined): item is RectangleItemObject => {
  return !!item?.isBackground && item.gitype === ITEM_TYPE.RECTANGLE;
};

export const getBackgroundItemObjects = (page: PageObject): ItemObject[] => {
  return Object.values(page.items.itemsHashMap).filter((item) => item.isBackground);
};

export const isBackgroundItemObjectTransparent = (item: ItemObject): boolean => {
  if (isColorBackgroundObject(item)) {
    return convertFabricColorToFillObject(item.fill).fillType === FillTypes.NONE;
  }
  if (isImageBackgroundObject(item)) {
    return item.transparency === transparencyStates.ON;
  }
  if (isVideoBackgroundObject(item)) {
    return item.hasTransparency || item.removeBackground.isBackgroundRemoved;
  }
  return false;
};

// A page renders with transparency if any of its (possibly timeline-sequenced) backgrounds is transparent.
export const isPageObjectTransparent = (page: PageObject): boolean => {
  return getBackgroundItemObjects(page).some(isBackgroundItemObjectTransparent);
};

export const getPagesArray = (pagesHashMap: Record<string, PageObject> | undefined, pageOrder: string[] | undefined): PageObject[] => {
  if (!pagesHashMap || !pageOrder) {
    return [];
  }
  return pageOrder.map((id) => pagesHashMap[id]).filter((page): page is PageObject => page !== undefined);
};

export const hasPageAnimation = (page: PageObject): boolean => {
  return Object.values(page.items.itemsHashMap).some((item) => item.introAnimation.type !== AnimationType.NONE || item.outroAnimation.type !== AnimationType.NONE);
};

export const hasItems = (page: PageObject): boolean => {
  return Object.keys(page.items.itemsHashMap).length > 0;
};

export const hasAudioClipsOnPage = (page: PageObject): boolean => {
  if (!page.audioClips) {
    return false;
  }

  return Object.keys(page.audioClips.audioItemsHashMap).length > 0;
};

// Every slideshow counts: single-slide slideshows — the one historically static case — no
// longer exist in migrated posters (MigrateToVersion73 dissolves them into plain items), and
// a freshly created one plays like any other timed item.
export const pageHasStreamingItem = (page: PageObject): boolean => {
  for (const item of Object.values(page.items.itemsHashMap)) {
    if (item.gitype === ITEM_TYPE.VIDEO || item.gitype === ITEM_TYPE.STICKER || item.gitype === ITEM_TYPE.TRANSCRIPT || item.gitype === ITEM_TYPE.SLIDESHOW) {
      return true;
    }
  }

  return false;
};


// More than one background means the backgrounds are tiled sequentially across
// the timeline, which makes the page play over time even when every background
// is a still image.
export const hasMultipleBackgrounds = (page: PageObject): boolean => {
  return getBackgroundItemObjects(page).length > 1;
};

/**
 * A background that occupies time on the background row. Backgrounds on a page with no
 * time-based content carry no span — end time 0, an invariant MigrateToVersion71 establishes
 * and the background write paths maintain — and don't count; a background whose span was laid
 * out on the timeline keeps the page timed even after the last video/audio is removed, so its
 * bar stays visible and editable instead of silently driving the page duration from behind a
 * hidden timeline. Stored `onPageEndTime` is what counts: a video background's derived span
 * would make every page holding one look authored, and it already makes the page a video
 * through the streaming-item clause.
 */
const hasTimedBackground = (page: PageObject): boolean => {
  return Object.values(page.items.itemsHashMap).some((item) => {
    return item.isBackground && item.onPageEndTime > PAGE_END_TIME_TOLERANCE_SECONDS;
  });
};

/**
 * Item timings the user authored on the timeline — any non-background item starting after 0,
 * or items whose ends differ from each other. The one nonzero shape this deliberately does
 * NOT count is every item uniformly spanning [0, D]: that is the automatic full-span
 * backfill left behind when a page's time base (video/audio) is removed, not authoring, so
 * such a page reads as static and the next time-based add redefines its duration. Authored
 * timings keep driving playback after the last video/audio is removed, so they must keep
 * the page a video — otherwise the timeline hides while items still appear and disappear
 * mid-playback with no way to see or edit why.
 *
 * Stored `onPageEndTime` is authoritative, mirroring PageVO::itemsCarryAuthoredTimings: items
 * whose span is derived rather than stored (videos, slideshows) would otherwise read as
 * authored next to items sitting at 0 — a plain single-slide slideshow on an untimed page
 * would be enough — and every such item already makes the page a video through an earlier clause.
 */
export const doItemsCarryAuthoredTimings = (page: PageObject): boolean => {
  let maxEndTime = 0;
  let minEndTime = Number.POSITIVE_INFINITY;
  let hasNonBackgroundItem = false;
  for (const item of Object.values(page.items.itemsHashMap)) {
    if (item.isBackground) {
      continue;
    }
    hasNonBackgroundItem = true;
    if (item.onPageStartTime > PAGE_END_TIME_TOLERANCE_SECONDS) {
      return true;
    }
    maxEndTime = Math.max(maxEndTime, item.onPageEndTime);
    minEndTime = Math.min(minEndTime, item.onPageEndTime);
  }
  if (!hasNonBackgroundItem || maxEndTime <= 0) {
    return false;
  }
  return maxEndTime - minEndTime > PAGE_END_TIME_TOLERANCE_SECONDS;
};

export const isPageVideo = (page: PageObject): boolean => {
  if (hasPageAnimation(page)) {
    return true;
  }

  if (hasAudioClipsOnPage(page)) {
    return true;
  }

  if (hasMultipleBackgrounds(page)) {
    return true;
  }

  if (hasTimedBackground(page)) {
    return true;
  }

  if (doItemsCarryAuthoredTimings(page)) {
    return true;
  }

  return pageHasStreamingItem(page);
};

export const pageObjectWithNewUids = (pageObject: PageObject): PageObject => {
  const newPageId = uuidv4();

  const newItemsHashMap: Record<string, ItemObject> = {};
  const itemsUidMap = new Map<string, string>();
  const itemsOrderLength = pageObject.items.itemsOrder?.length ?? 0;
  const newItemsOrder: string[] = new Array(itemsOrderLength);

  for (const [oldItemUid, itemObject] of Object.entries(pageObject.items.itemsHashMap)) {
    const newItemUid = uuidv4();
    itemsUidMap.set(oldItemUid, newItemUid);

    let newItemObject: ItemObject = {
      ...itemObject,
      uid: newItemUid,
    };

    if (itemObject.gitype === ITEM_TYPE.SLIDESHOW) {
      const slideshowItem = itemObject as SlideshowItemObject;
      if (slideshowItem.slides?.slidesHashMap) {
        const newSlidesHashMap: Record<string, SlideItemObject> = {};
        const slidesOrderLength = slideshowItem.slides.slidesOrder?.length ?? 0;
        const newSlidesOrder: string[] = new Array(slidesOrderLength);
        const slidesUidMap = new Map<string, string>();

        for (const [oldSlideUid, slideObject] of Object.entries(slideshowItem.slides.slidesHashMap)) {
          const newSlideUid = uuidv4();
          slidesUidMap.set(oldSlideUid, newSlideUid);
          newSlidesHashMap[newSlideUid] = {
            ...slideObject,
            uid: newSlideUid,
          };
        }

        for (let i = 0; i < slidesOrderLength; i++) {
          const oldSlideUid = slideshowItem.slides.slidesOrder[i];
          if (oldSlideUid) {
            const newSlideUid = slidesUidMap.get(oldSlideUid);
            if (newSlideUid !== undefined) {
              newSlidesOrder[i] = newSlideUid;
            }
          }
        }

        newItemObject = {
          ...newItemObject,
          slides: {
            ...slideshowItem.slides,
            slidesHashMap: newSlidesHashMap,
            slidesOrder: newSlidesOrder.filter(Boolean),
          },
          selectedSlideUID: '',
        } as ItemObject;
      }
    }

    if (itemObject.gitype === ITEM_TYPE.TRANSCRIPT) {
      const transcriptItem = itemObject as TranscriptItemObject;
      if (transcriptItem.subtitlesHashmap) {
        const newSubtitlesHashmap: Record<string, SubtitleObject> = {};
        for (const [, subtitleObject] of Object.entries(transcriptItem.subtitlesHashmap)) {
          const newSubtitleUid = uuidv4();
          newSubtitlesHashmap[newSubtitleUid] = {
            ...subtitleObject,
            subtitleUID: newSubtitleUid,
          };
        }
        newItemObject = {
          ...newItemObject,
          subtitlesHashmap: newSubtitlesHashmap,
        } as ItemObject;
      }
    }

    newItemsHashMap[newItemUid] = newItemObject;
  }

  for (let i = 0; i < itemsOrderLength; i++) {
    const oldItemUid = pageObject.items.itemsOrder[i];
    if (oldItemUid) {
      const newItemUid = itemsUidMap.get(oldItemUid);
      if (newItemUid !== undefined) {
        newItemsOrder[i] = newItemUid;
      }
    }
  }

  return {
    ...pageObject,
    hashedID: newPageId,
    items: {
      itemsHashMap: newItemsHashMap,
      itemsOrder: newItemsOrder.filter(Boolean),
      groups: pageObject.items.groups,
    },
  };
};
